import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ClipboardPaste } from 'lucide-react';
import { Text } from '../../base/Text';
import { Input } from '../../base/Input';
import { Button } from '../../base/Button';
import { Spinner } from '../../base/Spinner';
import { GroupSelector, type GroupSelectorGroup } from '../GroupSelector';
import styles from './AddLinkModal.module.css';

interface AddLinkModalProps {
  isOpen: boolean;
  isLoading?: boolean;
  initialUrl?: string;
  initialContextGroupId?: string | null;
  groups?: GroupSelectorGroup[];
  onClose: () => void;
  onSubmit: (url: string, contextGroupId?: string | null) => void;
}

const isValidUrl = (string: string): boolean => {
  try {
    const cleaned = string.trim();
    if (!cleaned) return false;
    if (cleaned.startsWith('http://') || cleaned.startsWith('https://')) {
      new URL(cleaned);
      return true;
    }
    const urlWithProtocol = `https://${cleaned}`;
    new URL(urlWithProtocol);
    const domain = cleaned.split('/')[0];
    return domain.includes('.') || domain === 'localhost' || domain.startsWith('localhost:');
  } catch {
    return false;
  }
};

export const AddLinkModal = ({
  isOpen,
  isLoading = false,
  initialUrl,
  initialContextGroupId,
  groups = [],
  onClose,
  onSubmit,
}: AddLinkModalProps) => {
  const { t } = useTranslation();
  const [url, setUrl] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(initialContextGroupId ?? null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setUrl(initialUrl || '');
      setSelectedGroupId(initialContextGroupId ?? null);
      setIsSubmitting(false);
      setError(null);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, initialUrl, initialContextGroupId]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isLoading && !isSubmitting) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isLoading, isSubmitting, onClose]);

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text?.trim()) setUrl(text.trim());
    } catch {
      setError(t('addLink.pasteFailed', 'Could not read clipboard'));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting || isLoading || !url.trim()) return;
    if (!isValidUrl(url.trim())) {
      setError(t('addLink.validUrlExample', 'Please enter a valid URL (e.g. https://example.com)'));
      return;
    }
    let finalUrl = url.trim();
    if (!finalUrl.startsWith('http://') && !finalUrl.startsWith('https://')) {
      finalUrl = 'https://' + finalUrl;
    }
    try {
      new URL(finalUrl);
    } catch {
      setError(t('addLink.invalidUrlFormat', 'Invalid URL format'));
      return;
    }
    setError(null);
    setIsSubmitting(true);
    onSubmit(finalUrl, selectedGroupId || undefined);
  };

  const handleClose = () => {
    if (!isLoading && !isSubmitting) onClose();
  };

  if (!isOpen) return null;
  const isProcessing = isLoading || isSubmitting;
  const showGroupSelector = groups.length > 0 && initialContextGroupId === undefined;

  return (
    <div
      className={styles.overlay}
      onClick={handleClose}
      style={{ cursor: isProcessing ? 'not-allowed' : 'default' }}
    >
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {isProcessing && (
          <div className={styles.loadingOverlay}>
            <Spinner size="lg" />
            <Text variant="body" className={styles.loadingText}>
              {t('addLink.saving')}
            </Text>
          </div>
        )}
        <Text variant="title" as="h2" className={styles.title}>
          {t('addLink.title')}
        </Text>
        <form onSubmit={handleSubmit}>
          <div className={styles.urlRow}>
            <div className={styles.urlInputWrap}>
              <Input
              ref={inputRef}
              type="url"
              placeholder={t('addLink.tapToPasteUrl', 'Tap to paste URL')}
              value={url}
              onChange={(e) => {
                setUrl(e.target.value);
                setError(null);
              }}
              fullWidth
              disabled={isProcessing}
              />
            </div>
            <button
              type="button"
              onClick={handlePaste}
              disabled={isProcessing}
              title={t('addLink.paste', 'Paste')}
              className={styles.pasteBtn}
              aria-label={t('addLink.paste', 'Paste')}
            >
              <ClipboardPaste size={20} />
            </button>
          </div>
          {error && (
            <Text variant="meta" className={styles.error}>
              {error}
            </Text>
          )}
          {showGroupSelector && (
            <div className={styles.groupSection}>
              <Text variant="meta" muted className={styles.groupLabel}>
                {t('linkDetail.group', 'Group')}
              </Text>
              <GroupSelector
                groups={groups}
                selectedGroupId={selectedGroupId}
                onSelectGroup={setSelectedGroupId}
              />
            </div>
          )}
          <div className={styles.actions}>
            <Button type="button" variant="ghost" onClick={handleClose} disabled={isProcessing}>
              {t('addLink.cancel')}
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={!url.trim() || isProcessing}
            >
              {isProcessing ? t('addLink.saving') : t('addLink.save')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

